/**
 * Fill in the blanks — dropdown mode player
 */

import * as React from 'react';
import {
    cn,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    shuffleArray,
} from '@ai-content-hub/content-kit';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import type { FITBElementData, FITBAnswer, FITBBlank } from './schema';
import { shouldShuffleOptions, fitbElementSchema } from './schema';
import { renderPassageWithBlanks } from './PassageRenderer';
import { resolveFitbPerBlankOk } from './perBlankFeedback';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import map from 'lodash/map';
import filter from 'lodash/filter';
import uniq from 'lodash/uniq';
import trim from 'lodash/trim';

export interface FITBPlayerDropdownProps
    extends ElementPlayerProps<FITBElementData, FITBAnswer> {
    flashBlankId?: string | null;
}

function optionsForBlank(blank: FITBBlank | undefined, shuffle: boolean): string[] {
    if (!blank) return [];
    const acc = [...(blank.acceptedAnswers ?? []), ...(blank.distractors ?? [])];
    const cleaned = filter(uniq(map(acc, (s) => trim(String(s)))), (s) => s !== '');
    if (shuffle) return shuffleArray([...cleaned]);
    return cleaned;
}

// Stable default (an inline `= {}` is a new object each render; keeps memo/effect deps stable).
const EMPTY_ANSWER: Record<string, string> = {};

export const FITBPlayerDropdown: React.FC<FITBPlayerDropdownProps> = ({
    data,
    answer = EMPTY_ANSWER,
    onAnswer,
    disabled = false,
    showFeedback = false,
    result,
    isReview = false,
    className,
    config,
    elementChildLabels,
    onFitbBlankAnchor,
    flashBlankId = null,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? fitbElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const review = showFeedback || isReview;
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false hides explanation/hints.
    const revealCorrect = config?.behavior?.showCorrectAnswer !== false;
    // ELMS-2370: hide answer => no per-blank right/wrong marks (learner choice still shown).
    const perOk = revealCorrect ? resolveFitbPerBlankOk(result, data, answer ?? {}, review) : undefined;
    const shuffle = shouldShuffleOptions(config);

    const optionsByBlank = React.useMemo(() => {
        const m: Record<number, string[]> = {};
        map(data.blanks, (b, i) => {
            m[i] = optionsForBlank(b, shuffle);
        });
        return m;
    }, [data.blanks, shuffle]);

    return (
        <div className={cn('fitb-player fitb-player-dropdown space-y-4', className)}>
            <div className="fitb-player-dropdown-passage rounded-lg border-0 bg-card p-4 shadow-sm">
                {renderPassageWithBlanks(data.passage, {
                    renderBlank: (idx, id) => {
                        const blank = data.blanks[idx];
                        const opts = optionsByBlank[idx] ?? [];
                        const val = answer[id] ?? '';
                        const ok = perOk?.[id];
                        const show = review && ok !== undefined;
                        const childN = elementChildLabels?.fitbBlank?.[id];
                        return (
                            <span
                                ref={(el) => onFitbBlankAnchor?.(id, el)}
                                className={cn(
                                    'inline-flex items-baseline gap-0.5 align-baseline max-w-full',
                                    flashBlankId === id &&
                                        'rounded-sm ring-2 ring-primary/50 ring-offset-1 transition-[box-shadow] duration-300'
                                )}
                            >
                                {childN != null ? (
                                    <span
                                        className="inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-md bg-muted-foreground/15 text-sm font-bold text-foreground"
                                        aria-hidden
                                    >
                                        {childN}
                                    </span>
                                ) : null}
                                <Select
                                value={val || undefined}
                                onValueChange={(v) => onAnswer?.({ ...answer, [id]: v })}
                                disabled={disabled || review || opts.length === 0}
                            >
                                <SelectTrigger
                                    className={cn(
                                        'fitb-player-dropdown-trigger inline-flex h-8 min-h-8 min-w-[15ch] w-[min(12rem,100%)] px-2 py-1 mx-0.5 align-baseline',
                                        show && ok && 'border-success ring-1 ring-success/40',
                                        show && !ok && 'border-destructive ring-1 ring-destructive/40',
                                        review &&
                                            'disabled:pointer-events-none disabled:!opacity-100 text-foreground [&>span]:text-foreground',
                                        show &&
                                            ok &&
                                            'text-success [&>span]:text-success [&_svg:not([class*=\'text-\'])]:text-success',
                                        show &&
                                            !ok &&
                                            'text-destructive [&>span]:text-destructive [&_svg:not([class*=\'text-\'])]:text-destructive'
                                    )}
                                    // EAA-2067: match the visible running blank number (childN)
                                    // so a screen reader does not restart at 1 per question.
                                    aria-label={`${t('blankNth')} ${childN ?? idx + 1}`}
                                >
                                    <SelectValue placeholder={blank?.placeholder || t('choosePlaceholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {map(opts, (opt, oi) => (
                                        <SelectItem key={`${id}-opt-${oi}`} value={opt}>
                                            {opt}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            </span>
                        );
                    },
                })}
            </div>
            {review && revealCorrect && data.explanation ? (
                <p className="fitb-player-dropdown-explanation text-sm text-muted-foreground border-t border-border pt-3">
                    {data.explanation}
                </p>
            ) : null}
        </div>
    );
};
